import * as THREE from 'three';
// Where an opening's bottom sits, and the one test behind it. In shared-lib
// because the question has four askers and one of them (the invariant test) runs
// on the functions side, which cannot load this module at all.
export {
  OPENING_ELEVATED_SILL_LIFT_M,
  elevatedSillLift,
  openingSillY as getOpeningSillY,
  isElevatedSillOpening,
  openingVerticalSpan,
} from '@shared-lib/geometry/openingSill';

/**
 * Wall Geometry Utilities
 *
 * Shared functions for calculating wall normals, coordinate systems,
 * and surface positions. Used by Door, Window, SceneObject, and PolygonWalls.
 */

/**
 * Calculate wall normal from wall's rotation field
 * @param {Object} wallRotation - Rotation object with x, y, z in radians
 * @returns {THREE.Vector3} Horizontal wall normal vector
 */
export const getWallNormalFromRotation = (wallRotation) => {
  const rotation = new THREE.Euler(
    wallRotation?.x || 0,
    wallRotation?.y || 0,
    wallRotation?.z || 0
  );

  // Get wall direction from rotation (wall runs along X axis in local space)
  const wallDir = new THREE.Vector3(1, 0, 0);
  wallDir.applyEuler(rotation);

  // Get wall normal (perpendicular) - 90° rotation in XZ plane
  const wallNormal = new THREE.Vector3(-wallDir.z, 0, wallDir.x);
  wallNormal.normalize();

  return wallNormal;
};

/**
 * Calculate wall normal from polygon corners
 * Returns horizontal normal (Y=0) for vertical walls
 * @param {Array} polygonCorners - Array of corner objects with x, y, z
 * @returns {THREE.Vector3|null} Horizontal wall normal vector or null if invalid
 */
export const getWallNormalFromPolygonCorners = (polygonCorners) => {
  if (!polygonCorners || polygonCorners.length < 3) {
    return null;
  }

  // Newell's method: accumulate the normal across every edge of the
  // polygon rather than from a single first-3-corner triangle. A
  // first-3-corner cross product silently degenerates to a zero vector
  // when those corners are collinear — which they are for perimeter
  // structure walls whose top-edge envelope (from
  // aggregateStructureWallCorners) puts several vertices along the
  // bottom edge. Newell's uses all corners, so it's immune to that.
  let nx = 0;
  let nz = 0;
  for (let i = 0; i < polygonCorners.length; i++) {
    const cur = polygonCorners[i];
    const next = polygonCorners[(i + 1) % polygonCorners.length];
    const curY = cur.y || 0;
    const nextY = next.y || 0;
    // Only the X and Z components of the Newell normal survive the
    // horizontal projection below, so we skip computing nY.
    nx += (curY - nextY) * (cur.z + next.z);
    nz += (cur.x - next.x) * (curY + nextY);
  }

  // For vertical walls the normal is horizontal; project to XZ.
  const horizontalNormal = new THREE.Vector3(nx, 0, nz);
  if (horizontalNormal.lengthSq() < 1e-12) {
    // Degenerate (e.g. all corners collinear or the polygon is itself
    // horizontal) — let the caller fall back to a rotation-based normal.
    return null;
  }
  return horizontalNormal.normalize();
};

/**
 * Get wall normal using best available data
 * Prefers polygonCorners if available, falls back to rotation
 * @param {Object} wall - Wall object with polygonCorners and/or rotation
 * @returns {THREE.Vector3|null} Wall normal or null if cannot be determined
 */
export const getWallNormal = (wall) => {
  if (!wall) return null;

  if (wall.polygonCorners && wall.polygonCorners.length >= 3) {
    const polygonNormal = getWallNormalFromPolygonCorners(wall.polygonCorners);
    // Degenerate polygon normal (collinear corners) falls through to the
    // rotation-based normal rather than returning a useless zero/null.
    if (polygonNormal) return polygonNormal;
  }

  if (wall.rotation) {
    return getWallNormalFromRotation(wall.rotation);
  }

  return null;
};

/**
 * Get wall coordinate system (normal, right, up vectors)
 * @param {Object} wall - Wall object with polygonCorners and/or rotation
 * @returns {Object|null} { normal, right, up } vectors or null
 */
export const getWallCoordinateSystem = (wall) => {
  const normal = getWallNormal(wall);
  if (!normal) return null;

  // Right vector is perpendicular to normal in XZ plane
  const right = new THREE.Vector3(normal.z, 0, -normal.x);
  right.normalize();

  // Up vector is always Y-up for vertical walls
  const up = new THREE.Vector3(0, 1, 0);

  return { normal, right, up };
};

/**
 * Calculate position correction to place an object flush with wall surface
 * For polygon-corner walls, uses the actual polygon plane
 * For rotation-based walls, uses rotation to calculate normal
 *
 * @param {Object} objectCenter - Object center with x, y, z
 * @param {Object} parentWall - Wall object with polygonCorners and/or rotation
 * @returns {Object|null} Corrected position {x, y, z} or null if cannot be calculated
 */
export const calculateWallSurfacePosition = (objectCenter, parentWall) => {
  if (!objectCenter || !parentWall) {
    return null;
  }

  let wallNormal;
  let wallSurfacePoint;

  // Check if wall has polygon corners - use actual geometry
  if (parentWall.polygonCorners && parentWall.polygonCorners.length >= 3) {
    // Calculate normal from actual polygon geometry
    wallNormal = getWallNormalFromPolygonCorners(parentWall.polygonCorners);

    // Use first corner as surface point
    const corner = parentWall.polygonCorners[0];
    wallSurfacePoint = new THREE.Vector3(
      corner.x,
      corner.y || objectCenter.y,
      corner.z
    );
  } else if (parentWall.rotation) {
    // Fallback: use rotation field for normal
    wallNormal = getWallNormalFromRotation(parentWall.rotation);

    if (parentWall.center) {
      wallSurfacePoint = new THREE.Vector3(
        parentWall.center.x,
        parentWall.center.y || objectCenter.y,
        parentWall.center.z
      );
    }
  }

  if (!wallNormal || !wallSurfacePoint) {
    return null;
  }

  // Calculate current object position
  const currentPos = new THREE.Vector3(
    objectCenter.x,
    objectCenter.y,
    objectCenter.z
  );

  // Calculate vector from wall surface point to current object position
  const toObject = new THREE.Vector3().subVectors(currentPos, wallSurfacePoint);

  // Project this vector onto the wall normal to get the perpendicular distance
  const perpDistance = toObject.dot(wallNormal);

  // Calculate correction to move object onto wall surface (distance should be 0)
  const correction = wallNormal.clone().multiplyScalar(-perpDistance);

  // Return corrected position (only moves along normal direction)
  return {
    x: objectCenter.x + correction.x,
    y: objectCenter.y, // Keep object's Y position (height on wall)
    z: objectCenter.z + correction.z,
  };
};

// Moved to shared-lib (pure; the functions-side take-off needs it).
export { getWallAxisXZ } from '@shared-lib/geometry/wallAxis';

/**
 * Get wall floor level (minimum Y) from wall data
 * Supports both polygon corner walls and rectangular walls (center/dimensions)
 *
 * @param {Object} wall - Wall object
 * @returns {number|null} Floor level Y coordinate or null
 */
export const getWallFloorLevel = (wall) => {
  // First try polygon corners (for polygon-defined walls)
  if (wall?.polygonCorners && wall.polygonCorners.length > 0) {
    // Find minimum Y from all polygon corners
    const yValues = wall.polygonCorners.map((corner) => corner.y || 0);
    return Math.min(...yValues);
  }

  // Fallback: rectangular wall defined by center and dimensions
  // Wall floor level = center.y - height/2
  if (wall?.center && wall?.dimensions) {
    const centerY = wall.center.y || 0;
    // dimensions can be array [x, y, z] or object {x, y, z}
    const height = Array.isArray(wall.dimensions)
      ? wall.dimensions[1] || 0
      : wall.dimensions.y || 0;
    return centerY - height / 2;
  }

  return null;
};
